import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { logAuditAction } from '@/hooks/useAuditLog';
import { paginateQuery } from '@/lib/paginateSupabase';

// DB → UI mappers
type RiskLevel = 'muy_bajo' | 'bajo' | 'medio' | 'alto' | 'muy_alto';
type RiskCategory = 'operacional' | 'tecnologico' | 'estrategico' | 'cumplimiento' | 'reputacional';
type ControlStatus = 'pendiente' | 'en_progreso' | 'implementado';

export interface DbRiskControl {
  id: string;
  risk_id: string;
  name: string;
  description: string | null;
  status: ControlStatus | null;
  probability_reduction: number | null;
  impact_reduction: number | null;
  responsible: string | null;
  due_date: string | null;
  source: 'manual' | 'ai' | 'import' | null;
}

export interface DbRiskProjectLink {
  id: string;
  risk_id: string;
  project_id: string;
  relevance: string | null;
  participant_projects?: { id: string; name: string } | null;
}

export interface DbRisk {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  organization_id: string;
  inherent_probability: string | null;
  inherent_impact: string | null;
  inherent_score: number | null;
  residual_probability: string | null;
  residual_impact: string | null;
  residual_score: number | null;
  objective_probability: string | null;
  objective_impact: string | null;
  objective_score: number | null;
  source: 'manual' | 'ai' | 'import' | null;
  ai_reasoning: string | null;
  created_at: string;
  risk_controls: DbRiskControl[];
  risk_project_links: DbRiskProjectLink[];
}

// ─── Queries ───

export function useRisks(organizationId: string) {
  return useQuery({
    queryKey: ['risks', organizationId],
    queryFn: async () => {
      const selectQuery = '*, risk_controls(*), risk_project_links(*, participant_projects(id, name))';

      let query = supabase.from('risks').select(selectQuery);

      if (organizationId && organizationId !== 'all') {
        query = query.eq('organization_id', organizationId);
      }

      const { data, error } = await query.order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []) as unknown as DbRisk[];
    },
    enabled: !!organizationId,
  });
}

export function useAvailableProjects(organizationId: string) {
  return useQuery({
    queryKey: ['available-projects-for-risk', organizationId],
    queryFn: async () => {
      const data = await paginateQuery<any>((from, to) =>
        supabase
          .from('participant_projects')
          .select('id, name, status, user_id, profiles!participant_projects_user_id_fkey(full_name, organization_id)')
          .range(from, to)
      );
      if (organizationId === 'all') return data;
      return data.filter((p: any) => p.profiles?.organization_id === organizationId);
    },
    enabled: !!organizationId,
  });
}

// ─── Mutations ───

export function useAddRisk() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (risk: {
      name: string;
      description: string;
      category: RiskCategory;
      organization_id: string;
      inherent_probability: RiskLevel;
      inherent_impact: RiskLevel;
      inherent_score: number;
      residual_probability: RiskLevel;
      residual_impact: RiskLevel;
      residual_score: number;
      source: 'manual' | 'ai';
      ai_reasoning?: string;
    }) => {
      const { data, error } = await supabase.from('risks').insert([risk]).select().single();
      if (error) throw error;
      if (user) {
        logAuditAction(user.id, 'risk.create', `Riesgo creado: ${risk.name}`, { risk_id: data.id }, risk.organization_id);
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['risks'] });
    },
  });
}

export function useUpdateRisk() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...fields }: { id: string; [key: string]: any }) => {
      const { data, error } = await supabase.from('risks').update(fields).eq('id', id).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['risks'] });
    },
  });
}

export function useDeleteRisk() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (riskId: string) => {
      const { error } = await supabase.from('risks').delete().eq('id', riskId);
      if (error) throw error;
      if (user) {
        logAuditAction(user.id, 'risk.delete', `Riesgo eliminado`, { risk_id: riskId });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['risks'] });
    },
  });
}

export function useAddControl() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (control: {
      risk_id: string;
      name: string;
      description?: string;
      status?: ControlStatus;
      probability_reduction?: number;
      impact_reduction?: number;
      responsible?: string;
      due_date?: string;
      source?: 'manual' | 'ai';
    }) => {
      const { data, error } = await supabase.from('risk_controls').insert([control]).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['risks'] });
    },
  });
}

export function useAddControlsBulk() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (controls: Array<{
      risk_id: string;
      name: string;
      description?: string;
      status?: ControlStatus;
      probability_reduction?: number;
      impact_reduction?: number;
      responsible?: string;
      source?: 'manual' | 'ai';
    }>) => {
      const { data, error } = await supabase.from('risk_controls').insert(controls).select();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['risks'] });
    },
  });
}

export function useUpdateControl() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...fields }: { id: string; status?: ControlStatus; probability_reduction?: number; impact_reduction?: number; responsible?: string; due_date?: string }) => {
      const { data, error } = await supabase.from('risk_controls').update(fields).eq('id', id).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['risks'] });
    },
  });
}

export function useDeleteControl() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (controlId: string) => {
      const { error } = await supabase.from('risk_controls').delete().eq('id', controlId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['risks'] });
    },
  });
}



export function useLinkProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (link: { risk_id: string; project_id: string; relevance?: string }) => {
      const { data, error } = await supabase.from('risk_project_links').insert([link]).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['risks'] });
    },
  });
}

export function useUnlinkProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (linkId: string) => {
      const { error } = await supabase.from('risk_project_links').delete().eq('id', linkId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['risks'] });
    },
  });
}
